import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Siren, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// ---- Loud siren via WebAudio (no file needed). Audio contexts must be
// unlocked by a user gesture, so we prime one on the first tap anywhere. ----
let audioCtx = null;
function primeAudio() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
  } catch { /* unsupported */ }
}
if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", primeAudio, { once: false, passive: true });
}

function startSiren() {
  try {
    primeAudio();
    if (!audioCtx || audioCtx.state !== "running") return null;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = "square";
    gain.gain.value = 0.9;
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    const t0 = audioCtx.currentTime;
    for (let i = 0; i < 120; i++) { // ~60s of two-tone sweep
      osc.frequency.setValueAtTime(750, t0 + i * 0.5);
      osc.frequency.linearRampToValueAtTime(1450, t0 + i * 0.5 + 0.4);
    }
    osc.start();
    return () => { try { osc.stop(); osc.disconnect(); gain.disconnect(); } catch { /* done */ } };
  } catch {
    return null;
  }
}

function vibrateLoop() {
  try { navigator.vibrate?.([600, 200, 600, 200, 600]); } catch { /* unsupported */ }
}

// ---- Full-screen takeover + persistent banner, polled for every user ----
export function EmergencyAlert() {
  const [state, setState] = useState(null); // {active, emergency, acked_by_me}
  const stopRef = useRef(null);
  const vibRef = useRef(null);

  const poll = async () => {
    try {
      const { data } = await api.get("/emergency/active");
      setState(data);
    } catch { /* offline */ }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 15000);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const alarming = state?.active && !state?.acked_by_me;

  useEffect(() => {
    if (alarming) {
      stopRef.current = startSiren();
      vibrateLoop();
      vibRef.current = setInterval(vibrateLoop, 2500);
    }
    return () => {
      stopRef.current?.();
      stopRef.current = null;
      if (vibRef.current) { clearInterval(vibRef.current); vibRef.current = null; }
      try { navigator.vibrate?.(0); } catch { /* noop */ }
    };
  }, [alarming]);

  if (!state?.active) return null;
  const e = state.emergency;
  const label = e.kind === "stop_works" ? "STOP WORKS" : "SITE SHUTDOWN";

  const ack = async () => {
    try {
      await api.post(`/emergency/${e.id}/ack`);
      setState((s) => ({ ...s, acked_by_me: true }));
    } catch { /* retried on next poll */ }
  };

  if (!alarming) {
    return (
      <div className="sticky top-0 z-[60] bg-red-600 text-white px-4 py-2.5 flex items-center gap-3 shadow-lg" data-testid="emergency-banner">
        <Siren className="w-5 h-5 animate-pulse shrink-0" />
        <div className="text-xs font-bold uppercase tracking-wider truncate flex-1">
          {label} ACTIVE — {e.message}{e.job_title ? ` (${e.job_title})` : ""}
        </div>
        <span className="text-[10px] font-mono uppercase opacity-80 shrink-0">Wait for all clear</span>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-red-600 flex flex-col items-center justify-center text-center p-6" data-testid="emergency-takeover">
      <Siren className="w-20 h-20 text-white mb-6 animate-pulse" strokeWidth={1.5} />
      <div className="text-white/90 font-mono text-xs uppercase tracking-[0.35em] mb-2">Emergency alert</div>
      <h1 className="text-white font-display font-black uppercase tracking-tight text-5xl leading-none">{label}</h1>
      <p className="text-white text-lg font-bold mt-5 max-w-md" data-testid="emergency-message">{e.message}</p>
      {e.job_title && <p className="text-white/90 text-sm mt-1.5 font-mono uppercase tracking-wider">{e.job_title}</p>}
      <p className="text-white/80 text-xs mt-2 font-mono">Raised by {e.created_by}</p>
      <button
        onClick={ack}
        className="mt-10 bg-white text-red-600 font-display font-black uppercase tracking-wide text-xl px-10 py-5 rounded-2xl shadow-2xl active:scale-95 transition-transform"
        data-testid="emergency-ack-button"
      >
        I'm safe — acknowledge
      </button>
    </div>
  );
}

// ---- Office trigger button + dialog (header) ----
export function EmergencyTrigger() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [kind, setKind] = useState("site_shutdown");
  const [message, setMessage] = useState("");
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [busy, setBusy] = useState(false);

  const check = () => api.get("/emergency/active").then((r) => setActive(r.data.active ? r.data.emergency : null)).catch(() => {});
  useEffect(() => { check(); }, [open]);

  if (!["admin", "office", "super_admin"].includes(user.role)) return null;

  const openDialog = async () => {
    setOpen(true);
    if (jobs.length === 0) api.get("/jobs").then((r) => setJobs(r.data || [])).catch(() => {});
  };

  const trigger = async () => {
    if (!message.trim()) { toast.error("Say what's happening — e.g. 'Gas leak, evacuate now'"); return; }
    if (!window.confirm("This will sound a loud emergency alarm on every worker's phone. Continue?")) return;
    setBusy(true);
    try {
      await api.post("/emergency", { kind, message: message.trim(), job_id: jobId || null });
      toast.success("Emergency alarm sounded");
      setMessage("");
      check();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not trigger emergency");
    } finally {
      setBusy(false);
    }
  };

  const allClear = async () => {
    if (!window.confirm("Stand down the emergency and notify everyone it's all clear?")) return;
    setBusy(true);
    try {
      await api.post(`/emergency/${active.id}/all-clear`);
      toast.success("All clear sent");
      check();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={openDialog}
        className={`relative w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
          active ? "bg-red-600 text-white animate-pulse" : "text-red-600 border border-red-200 dark:border-red-900/60 hover:bg-red-50 dark:hover:bg-red-950/40"
        }`}
        title="Emergency"
        aria-label="Emergency"
        data-testid="emergency-trigger-button"
      >
        <Siren className="w-[18px] h-[18px]" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="emergency-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-red-600 flex items-center gap-2">
              <Siren className="w-5 h-5" /> Emergency
            </DialogTitle>
            <DialogDescription>
              Sounds a loud alarm and full-screen alert on every worker's phone, plus a push notification.
            </DialogDescription>
          </DialogHeader>

          {active ? (
            <div className="space-y-4">
              <div className="border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 rounded-md p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-red-600">{active.kind === "stop_works" ? "Stop works" : "Site shutdown"} active</div>
                <div className="text-sm mt-1 font-medium">{active.message}</div>
                <div className="text-[11px] font-mono text-zinc-500 mt-1">Raised by {active.created_by}</div>
              </div>
              <div>
                <div className="label-tag mb-2">Acknowledged ({(active.acks || []).length})</div>
                {(active.acks || []).length === 0 ? (
                  <div className="text-xs text-zinc-500 italic">No one has acknowledged yet.</div>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {active.acks.map((a) => (
                      <div key={a.user_id} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> {a.name}
                        <span className="text-[10px] font-mono text-zinc-400">{new Date(a.at).toLocaleTimeString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={allClear} disabled={busy} className="w-full rounded-sm bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider" data-testid="emergency-all-clear-button">
                {busy ? "Sending…" : "All clear — stand down"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="label-tag">Type</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="emergency-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="site_shutdown">Site shutdown (evacuate)</SelectItem>
                    <SelectItem value="stop_works">Stop works (down tools)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tag">What's happening? *</Label>
                <Input value={message} onChange={(e) => setMessage(e.target.value)}
                       placeholder="e.g. Gas main struck — evacuate to muster point"
                       className="mt-1.5 rounded-sm border-zinc-400" data-testid="emergency-message-input" />
              </div>
              <div>
                <Label className="label-tag">Site / job (optional)</Label>
                <Select value={jobId || "__all__"} onValueChange={(v) => setJobId(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="emergency-job-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All sites / whole company</SelectItem>
                    {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={trigger} disabled={busy}
                      className="w-full rounded-sm bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-wider h-12 text-base"
                      data-testid="emergency-sound-alarm-button">
                {busy ? "Sounding…" : "🚨 Sound the alarm"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
